import { appInstanceMap } from '../create_app'
import { CompletionPath, isSafari, pureCreateElement, getLinkFileDir } from '../libs/utils'

// https://developer.mozilla.org/zh-CN/docs/Web/API/CSSRule
enum CSSRuleType {
  STYLE_RULE = 1,
  MEDIA_RULE = 4,
  // FONT_FACE_RULE = 5,
  SUPPORTS_RULE = 12,
}

/**
 * 绑定css作用域
 * 特殊情况
 * 1. html-abc | abc-html
 * 2. html body.abc
 * 3. abchtml | htmlabc | abcbody | bodyabc
 * 4. html + body | html > body | html.body | html[name=xx] | body[name=xx]
 * 5. xxx, html xxx, body xxx
 *
 * TODO: BUG
  .test-b {
    border: 1px solid var(--color-a);
    border-bottom-color: var(--color-b);
  }
 */
function scopedStyleRule (rule: CSSStyleRule, prefix: string): string {
  const { selectorText, cssText } = rule
  if (/^((html[\s>~,]+body)|(html|body|:root))$/.test(selectorText)) {
    return cssText.replace(/^((html[\s>~,]+body)|(html|body|:root))/, prefix)
  } else if (selectorText === '*') {
    return cssText.replace('*', `${prefix} *`)
  }

  const builtInRootSelectorRE = /(^|\s+)((html[\s>~]+body)|(html|body|:root))(?=[\s>~]+|$)/

  return cssText.replace(/^[\s\S]+{/, (selectors) => {
    return selectors.replace(/(^|,)([^,]+)/g, (all, $1, $2) => {
      if (builtInRootSelectorRE.test($2)) {
        // body[name=xx]|body.xx|body#xx 等都不需要转换
        return all.replace(builtInRootSelectorRE, prefix)
      }
      return `${$1} ${prefix} ${$2.replace(/^\s*/, '')}`
    })
  })
}

/**
 * 补全静态资源地址
 * @param cssText css内容
 * @param baseURI 域名
 * @param textContent 原始内容
 * @param linkpath link资源地址，如果是link转换为的style，会带有linkpath
 */
function scopedHost (
  cssText: string,
  baseURI: string,
  textContent: string,
  linkpath: string | undefined,
) {
  return cssText.replace(/url\(["']?([^)"']+)["']?\)/gm, (all, $1) => {
    if (/^data:/.test($1)) {
      return all
    } else if (/^(https?:)?\/\//.test($1)) {
      // Safari会自动将cssRule的静态资源进行域名补全，需要单独处理
      if (isSafari()) {
        const purePath = $1.replace(/^https?:/, '')
        // 原始内容没有当前地址，则为浏览器补全地址
        if (textContent.indexOf(purePath) === -1) {
          $1 = $1.replace(window.location.origin, '')
        } else {
          return all
        }
      } else {
        return all
      }
    }

    // 如果是link资源且url是相对地址，则使用link的文件夹地址baseURI
    // ./a/b.png  ../a/b.png  a/b.png
    if (/^((\.\.?\/)|[^/])/.test($1) && linkpath) {
      baseURI = getLinkFileDir(linkpath)
    }

    return `url("${CompletionPath($1, baseURI)}")`
  })
}

// 处理media 和 supports
function scopedPackRule (
  rule: CSSMediaRule | CSSSupportsRule,
  prefix: string,
  packName: string,
): string {
  const result = scopedRule(Array.from(rule.cssRules), prefix)
  return `@${packName} ${rule.conditionText} {${result}}`
}

/**
 * 依次处理每个cssRule
 * @param rules cssRule
 * @param prefix 前缀
 */
function scopedRule (rules: CSSRule[], prefix: string): string {
  let result = ''
  for (const rule of rules) {
    switch (rule.type) {
      case CSSRuleType.STYLE_RULE:
        result += scopedStyleRule(rule as CSSStyleRule, prefix)
        break
      case CSSRuleType.MEDIA_RULE:
        result += scopedPackRule(rule as CSSMediaRule, prefix, 'media')
        break
      case CSSRuleType.SUPPORTS_RULE:
        result += scopedPackRule(rule as CSSSupportsRule, prefix, 'supports')
        break
      // case CSSRuleType.FONT_FACE_RULE:
      //   result += scopedHost(rule.cssText, baseURI, textContent)
      //   break
      default:
        result += rule.cssText
        break
    }
  }

  return result
}

/**
 * 绑定css通用方法
 */
function commonAction (
  packStyle: HTMLStyleElement,
  styleElement: HTMLStyleElement,
  originContent: string,
  prefix: string,
  baseURI: string,
  linkpath: string | undefined,
) {
  /**
   * safari14.0.1 macos11.0.1 big sur sheet中unicode变成乱码，需要特殊处理
   * 因为unicode的引号丢失，content: "\e940" 变成 content: ◻︎，导致失效。
   * content: 'xx' 这样也会出问题，转换之后变为 content: xx，导致失效
   * 但如果中间有特殊字符，如空格、括号 等，就没问题了 content: 'x x'
   * content的值多变，还包含一些关键字、表情等特殊情况
   * 参考文档 https://developer.mozilla.org/zh-CN/docs/Web/CSS/content
   * 如果依然有问题，推荐使用attr()方案降级处理
   *
   * TODO:
   * 1、测试其它版本的safari和ios
   */
  packStyle.textContent = originContent
  const rules: CSSRule[] = Array.from(packStyle.sheet?.cssRules ?? [])
  let result = scopedHost(
    scopedRule(rules, prefix),
    baseURI,
    originContent,
    linkpath,
  )
  // safari浏览器补全丢失的引号
  if (isSafari()) {
    result = result.replace(/([;{]\s*content:\s*)([^\s"][^";}]*)/gm, (all, $1, $2) => {
      if (
        $2 === 'none' ||
        /^(url\()|(counter\()|(attr\()|(open-quote)|(close-quote)/.test($2)
      ) {
        return all
      }
      return `${$1}"${$2}"`
    })
  }
  styleElement.textContent = result
  packStyle.textContent = ''
}

let packStyle: HTMLStyleElement

/**
 * 绑定css作用域
 * @param styleElement 目标style元素
 * @param appName 应用名称
 */
export default function scopedCSS (styleElement: HTMLStyleElement, appName: string): HTMLStyleElement {
  const app = appInstanceMap.get(appName)
  if (app?.scopecss) {
    const prefix = `micro-app[name=${appName}]`
    if (!packStyle) {
      packStyle = pureCreateElement('style')
      document.body.appendChild(packStyle)
      packStyle.sheet!.disabled = true
    }

    const content = styleElement.textContent ?? ''

    if (content) {
      commonAction(
        packStyle,
        styleElement,
        content,
        prefix,
        app.url,
        // 补全链接地址的协议 //jd.com/index.css > http://jd.com/index.css
        // 样式表中存在需要补全域名（url(./xxx.png)）的情况下无协议地址会报错
        // new URL('xxx', '//jd.com/') 会报错
        CompletionPath(styleElement.linkpath, app.url, true)
      )
    } else {
      const observer = new MutationObserver(function () {
        observer.disconnect()
        /**
         * 解决styled-component引发的问题：
         * const styleRef = document.createElement('style')
         * document.head.appendChild(styleRef)
         * styleRef.appendChild(document.createTextNode("")) // 如果没有这一行，则无法监听到style的变化
         * const oldSheet = styleRef.sheet
         * oldSheet.inserRules(CSSRuleItem, [index])
         *
         * styleRef.textContent = 'a {color: red}'
         * styleRef.sheet !== oldSheet // true 被复写后对于oldSheet的操作则不会被展示在页面上
         *
         * 对于这种情况放弃为CSS添加作用域
         */
        /**
         * FIXME: 当前只能通过style标签没有content和style.sheet.cssRules.length > 0
         * 判断当前style标签是styled-component创建的，如果有更好的方法，请修改本判断
         */
        /**
         * 注意：这段代码无法移动到MutationObserver外面判断
         */
        if (!styleElement.textContent && styleElement && styleElement.sheet && (styleElement.sheet.cssRules.length > 0)) return

        commonAction(packStyle, styleElement, styleElement.textContent ?? '', prefix, app.url, styleElement.linkpath)
      })

      observer.observe(styleElement, { childList: true })
    }
  }

  return styleElement
}
